import {
    connectAuthEmulator,
    getAuth,
    GoogleAuthProvider,
    signInWithPopup,
} from '../infrastructure/firebase-sdk.js';
import { AuthHintModule } from './auth-hint.js';
import { EMULATOR_HOSTS, isEmulatorMode } from '../infrastructure/emulator.js';

export class AuthModule {
    constructor(firebaseApp) {
        this.auth = getAuth(firebaseApp);
        if (isEmulatorMode()) {
            connectAuthEmulator(this.auth, EMULATOR_HOSTS.auth, { disableWarnings: true });
            console.info(`[auth] connected to emulator at ${EMULATOR_HOSTS.auth}`);
        }
        this.provider = new GoogleAuthProvider();
        this.currentUser = null;
        this.authStateCallbacks = [];
        this.authInitialized = false;
        this.initializationPromise = null;

        // Track initialization state
        this.initializationPromise = new Promise((resolve) => {
            this.auth.onAuthStateChanged((user) => {
                this.currentUser = user;

                // Mark as initialized on first auth state change
                if (!this.authInitialized) {
                    this.authInitialized = true;
                    resolve(user);
                }

                // Notify all callbacks
                this.authStateCallbacks.forEach((callback) => callback(user));
            });
        });
    }

    async signInWithGoogle() {
        try {
            const result = await signInWithPopup(this.auth, this.provider);
            // Save auth hint for instant rendering on reload
            AuthHintModule.saveHint(result.user);
            return {
                success: true,
                user: result.user,
                message: `Welcome, ${result.user.displayName}!`,
            };
        } catch (error) {
            return {
                success: false,
                error: error,
                message: error.message,
            };
        }
    }

    async signOut() {
        try {
            await this.auth.signOut();
            // Clear auth hint on sign out
            AuthHintModule.clearHint();
            return {
                success: true,
                message: 'Signed out successfully',
            };
        } catch (error) {
            return {
                success: false,
                error: error,
                message: error.message,
            };
        }
    }

    onAuthStateChanged(callback) {
        this.authStateCallbacks.push(callback);
        // Always call callback immediately with current state (even if null)
        callback(this.currentUser);
        return () => {
            const index = this.authStateCallbacks.indexOf(callback);
            if (index > -1) {
                this.authStateCallbacks.splice(index, 1);
            }
        };
    }

    getCurrentUser() {
        return this.currentUser;
    }

    isAuthenticated() {
        return this.currentUser !== null;
    }

    /**
     * Wait for Firebase Auth to complete its initial state check
     * This prevents the "flash of unauthenticated content" (FOUC)
     * by ensuring we know the auth state before showing any content
     *
     * @returns {Promise<import('firebase/auth').User|null>} Resolves with the user, or null
     */
    async waitForAuthInitialization() {
        return this.initializationPromise;
    }
}
